import { Injectable } from '@angular/core';
import { tap } from 'rxjs';
import jsPDF from 'jspdf';
import { CompanyService } from './service';
import { Position } from '../api/data-model';
import { AddressSuiteTypeList } from '../api/enum/address-suite-type';
import { FNEducationList } from '../api/enum/fn-education';

type LayoutType = 'TWO_COLUMNS' | 'FULL_WIDTH';
type BorderType = 'COMPLETE' | 'FIRST_PAGE' | 'MIDDLE_PAGE' | 'LAST_PAGE';

interface ContentItem {
  label: string;
  content: string | number | undefined;
  layout: LayoutType;
  isSubSectionTitle?: boolean;  // Mark as sub-section title
  indentLevel?: number;         // Indent level: 0=normal, 1=sub-section
  showDividerLine?: boolean;    // Show divider line after sub-section title
}

interface ProcessedText {
  lines: string[];           // Pre-split lines for PDF rendering
  totalHeight: number;       // Total height including spacing
  lineHeight: number;        // Height per line
  paragraphBreaks: number[]; // Indices where paragraph breaks occur
  labelHeight: number;       // Height of the label
}

interface SectionRenderOptions {
  forceSpanning?: boolean;
  customContentRenderer?: (pdf: jsPDF, items: ContentItem[], startY: number, maxY: number) => number;
  borderStyle?: {
    color?: string;
    width?: number;
    cornerRadius?: number;
  };
}

interface SectionPaginationInfo {
  needsPagination: boolean;
  totalHeight: number;
  availableHeight: number;
}


@Injectable({
  providedIn: 'root'
})
export class PdfService {
  // PDF Layout Constants
  private readonly TOP_MARGIN = 20;
  private readonly BOTTOM_MARGIN = 20;
  private readonly LEFT_MARGIN = 20;
  private readonly RIGHT_MARGIN = 20;
  private readonly SECTION_MARGIN = 5;
  private readonly SECTION_WIDTH = 170;
  private readonly CONTENT_LEFT_MARGIN = 25;
  private readonly CONTENT_RIGHT_MARGIN = 110;
  private readonly FOOTER_MARGIN = 10;
  private readonly FOOTER_RIGHT_OFFSET = 40;
  private readonly NO_HEADER_OFFSET = 10;
  private readonly TITLE_HEIGHT = 8;
  private readonly DIVIDER_SPACING = 5;
  private readonly CONTENT_SPACING = 5;
  private readonly LINE_HEIGHT = 6;
  private readonly BOTTOM_PADDING = 10;
  private readonly SECTION_TITLE_HEIGHT = this.TITLE_HEIGHT + this.DIVIDER_SPACING;
  private readonly CONTENT_START_OFFSET = this.SECTION_TITLE_HEIGHT + this.CONTENT_SPACING;
  private readonly LABEL_COLOR = '#204496';
  private readonly CONTENT_COLOR = '#334155';
  private readonly SUB_SECTION_DIVIDER_LENGTH = 120; // Length of sub-section divider line (shorter than main section)

  constructor(private readonly companyService: CompanyService) { }

  // Layout constants for consistent spacing
  private readonly TWO_COLUMN_HEIGHT = this.LINE_HEIGHT * 2; // Label + content
  private readonly TWO_COLUMN_SPACING = 3; // Comfortable spacing between rows
  private readonly SECTION_PADDING_TOP = 2; // Top padding inside sections
  private readonly SECTION_PADDING_BOTTOM = 3; // Bottom padding inside sections
  private readonly PARAGRAPH_SPACING = 3; // Extra spacing between paragraphs

  // ========================================
  // IMPROVED TEXT PROCESSING SYSTEM
  // ========================================

  /**
   * Preprocess text to maintain original formatting and prepare for accurate PDF rendering
   */
  private preprocessText(
    pdf: jsPDF,
    text: string | undefined,
    maxWidth: number,
    label: string
  ): ProcessedText {
    if (!text) {
      return {
        lines: [],
        totalHeight: this.LINE_HEIGHT, // Just label height
        lineHeight: this.LINE_HEIGHT,
        paragraphBreaks: [],
        labelHeight: this.LINE_HEIGHT
      };
    }

    // Set font size for accurate text measurement
    pdf.setFontSize(10);

    // Preserve original paragraph structure
    const paragraphs = text.split(/\n\s*\n/); // Split on double newlines (paragraph breaks)
    const allLines: string[] = [];
    const paragraphBreaks: number[] = [];

    paragraphs.forEach((paragraph, paragraphIndex) => {
      // Handle single line breaks within paragraphs - preserve numbered lists
      const paragraphLines = paragraph.split(/\n/);

      paragraphLines.forEach((line, lineIndex) => {
        const trimmedLine = line.trim();
        if (trimmedLine) {
          // Split each line based on PDF width
          const lines = pdf.splitTextToSize(trimmedLine, maxWidth);
          allLines.push(...lines);
        }
      });

      // Mark paragraph end (except for last paragraph)
      if (paragraphIndex < paragraphs.length - 1) {
        paragraphBreaks.push(allLines.length - 1);
      }
    });

    // Calculate total height including paragraph spacing
    const contentHeight = allLines.length * this.LINE_HEIGHT;
    const paragraphSpacing = paragraphBreaks.length * this.PARAGRAPH_SPACING;
    const totalHeight = this.LINE_HEIGHT + contentHeight + paragraphSpacing;

    return {
      lines: allLines,
      totalHeight: totalHeight,
      lineHeight: this.LINE_HEIGHT,
      paragraphBreaks: paragraphBreaks,
      labelHeight: this.LINE_HEIGHT
    };
  }

  /**
   * Render processed text with intelligent pagination
   */
  private renderProcessedText(
    pdf: jsPDF,
    label: string,
    processedText: ProcessedText,
    x: number,
    startY: number,
    maxY: number
  ): { finalY: number; completed: boolean; remainingText?: ProcessedText } {
    pdf.setFontSize(10);

    // Render label
    pdf.setTextColor(this.LABEL_COLOR);
    pdf.setFont('helvetica', 'bold'); // Set label font to bold
    pdf.text(`${label}:`, x, startY);
    pdf.setFont('helvetica', 'normal'); // Reset font to normal

    let currentY = startY + this.LINE_HEIGHT;
    const availableSpace = maxY - currentY;
    const availableLines = Math.floor(availableSpace / this.LINE_HEIGHT);

    if (processedText.lines.length === 0) {
      // No content to render
      return { finalY: currentY, completed: true };
    }

    // Check if all content fits
    const requiredSpace = this.calculateProcessedTextHeight(processedText);
    if (requiredSpace <= availableSpace) {
      // Render all content
      currentY = this.renderProcessedTextLines(pdf, processedText, x, currentY);
      return { finalY: currentY, completed: true };
    }

    // Content needs to be split - find optimal split point
    const splitPoint = this.findOptimalSplitPoint(processedText, availableLines);

    // Render first part
    const firstPart: ProcessedText = {
      lines: processedText.lines.slice(0, splitPoint.lineIndex),
      totalHeight: splitPoint.lineIndex * this.LINE_HEIGHT,
      lineHeight: this.LINE_HEIGHT,
      paragraphBreaks: processedText.paragraphBreaks.filter(bp => bp < splitPoint.lineIndex),
      labelHeight: 0 // No label for continuation
    };

    currentY = this.renderProcessedTextLines(pdf, firstPart, x, currentY);

    // Prepare remaining content
    const remainingLines = processedText.lines.slice(splitPoint.lineIndex);
    const remainingText: ProcessedText = {
      lines: remainingLines,
      totalHeight: remainingLines.length * this.LINE_HEIGHT,
      lineHeight: this.LINE_HEIGHT,
      paragraphBreaks: processedText.paragraphBreaks
        .filter(bp => bp >= splitPoint.lineIndex)
        .map(bp => bp - splitPoint.lineIndex),
      labelHeight: 0
    };

    return { finalY: currentY, completed: false, remainingText };
  }

  /**
   * Calculate the height required for processed text
   */
  private calculateProcessedTextHeight(processedText: ProcessedText): number {
    const linesHeight = processedText.lines.length * this.LINE_HEIGHT;
    const paragraphSpacing = processedText.paragraphBreaks.length * this.PARAGRAPH_SPACING;
    return linesHeight + paragraphSpacing;
  }

  /**
   * Find optimal split point to avoid breaking paragraphs
   */
  private findOptimalSplitPoint(
    processedText: ProcessedText,
    maxLines: number
  ): { lineIndex: number; preservesParagraph: boolean } {
    if (maxLines >= processedText.lines.length) {
      return { lineIndex: processedText.lines.length, preservesParagraph: true };
    }

    // Try to find a paragraph break near the max lines
    const searchRange = Math.min(5, Math.floor(maxLines * 0.2)); // Search within 20% range

    for (let i = 0; i < searchRange; i++) {
      const checkIndex = maxLines - i;
      if (checkIndex > 0 && processedText.paragraphBreaks.includes(checkIndex - 1)) {
        return { lineIndex: checkIndex, preservesParagraph: true };
      }
    }

    // No suitable paragraph break found, split at max lines
    return { lineIndex: maxLines, preservesParagraph: false };
  }

  /**
   * Render processed text lines with paragraph spacing
   */
  private renderProcessedTextLines(
    pdf: jsPDF,
    processedText: ProcessedText,
    x: number,
    startY: number
  ): number {
    pdf.setFontSize(10);
    pdf.setTextColor(this.CONTENT_COLOR);

    let currentY = startY;

    processedText.lines.forEach((line, index) => {
      pdf.text(line, x, currentY);
      currentY += this.LINE_HEIGHT;

      // Add paragraph spacing after paragraph breaks
      if (processedText.paragraphBreaks.includes(index)) {
        currentY += this.PARAGRAPH_SPACING;
      }
    });

    return currentY;
  }

  /**
   * Render remaining processed text without label
   */
  private renderRemainingProcessedText(
    pdf: jsPDF,
    remainingText: ProcessedText,
    x: number,
    startY: number
  ): number {
    return this.renderProcessedTextLines(pdf, remainingText, x, startY);
  }

  // Improved unified rendering method with pagination support
  private renderContentItems(
    pdf: jsPDF,
    items: ContentItem[],
    startY: number,
    maxY?: number
  ): number {
    const pageHeight = pdf.internal.pageSize.height;
    const effectiveMaxY = maxY || (pageHeight - this.BOTTOM_MARGIN);

    let currentY = startY + this.SECTION_PADDING_TOP; // Add top padding
    let i = 0;

    while (i < items.length) {
      if (items[i].layout === 'FULL_WIDTH') {
        // Calculate required height for full-width item
        const itemHeight = this.calculateItemHeight(pdf, items[i]);
        const totalHeight = itemHeight + this.CONTENT_SPACING;

        // Check if item fits in current page
        if (currentY + totalHeight > effectiveMaxY) {
          // Item doesn't fit, check if we can split it
          const availableSpace = effectiveMaxY - currentY;

          if (availableSpace > this.LINE_HEIGHT * 3) { // Minimum space for meaningful content
            // Split the item across pages
            const splitResult = this.renderItemWithSplit(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'FULL_WIDTH', availableSpace);

            if (splitResult.completed) {
              // Item was fully rendered in available space
              currentY += splitResult.usedHeight + this.CONTENT_SPACING;
            } else {
              // Item was split, continue on next page
              pdf.addPage();
              currentY = this.TOP_MARGIN;

              // Render remaining content
              const remainingHeight = this.renderItemRemaining(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'FULL_WIDTH', splitResult.remainingContent, splitResult.remainingText);
              currentY += remainingHeight + this.CONTENT_SPACING;
            }
          } else {
            // Not enough space, move to next page
            pdf.addPage();
            currentY = this.TOP_MARGIN;

            // Render full item on new page
            const height = this.renderItem(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'FULL_WIDTH');
            currentY += height + this.CONTENT_SPACING;
          }
        } else {
          // Render single column item normally
          const height = this.renderItem(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'FULL_WIDTH');
          currentY += height + this.CONTENT_SPACING;
        }
        i++;
      } else {
        // Handle two column items
        const requiredHeight = this.TWO_COLUMN_HEIGHT + this.TWO_COLUMN_SPACING;

        // Check if two-column pair fits in current page
        if (currentY + requiredHeight > effectiveMaxY) {
          pdf.addPage();
          currentY = this.TOP_MARGIN;
        }

        // Render two column items
        this.renderItem(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'TWO_COLUMNS');

        // Render right column if exists
        if (i + 1 < items.length && items[i + 1].layout === 'TWO_COLUMNS') {
          this.renderItem(pdf, items[i + 1], this.CONTENT_RIGHT_MARGIN, currentY, 'TWO_COLUMNS');
          i += 2;
        } else {
          i++;
        }

        currentY += this.TWO_COLUMN_HEIGHT + this.TWO_COLUMN_SPACING;
      }
    }

    return currentY + this.SECTION_PADDING_BOTTOM; // Add bottom padding
  }

  // Calculate item height without rendering
  private calculateItemHeight(pdf: jsPDF, item: ContentItem): number {
    // Handle sub-section titles
    if (item.isSubSectionTitle) {
      let height = this.LINE_HEIGHT + 3; // Title height plus spacing
      if (item.showDividerLine) {
        height += 4; // Add height for divider line and spacing
      }
      return height;
    }

    // Calculate indent offset
    const indentOffset = (item.indentLevel || 0) * 10;  // 10 units per indent level

    // Calculate content width based on layout and indent
    const baseWidth = item.layout === 'FULL_WIDTH'
      ? this.SECTION_WIDTH - 10 // Use section width minus small margin for full width
      : (this.CONTENT_RIGHT_MARGIN - this.CONTENT_LEFT_MARGIN) / 2;
    const maxWidth = baseWidth - indentOffset; // Reduce width by indent

    const contentText = `${item.content ?? ''}`;
    const lines = pdf.splitTextToSize(contentText, maxWidth);
    return this.LINE_HEIGHT + (lines.length * this.LINE_HEIGHT);
  }

  // Render item with splitting capability using improved text processing
  private renderItemWithSplit(
    pdf: jsPDF,
    item: ContentItem,
    x: number,
    y: number,
    layout: LayoutType,
    availableSpace: number
  ): { completed: boolean; usedHeight: number; remainingContent: string; remainingText?: ProcessedText } {
    // Calculate indent offset for width calculation
    const indentOffset = (item.indentLevel || 0) * 10;
    const renderX = x + indentOffset;

    const baseWidth = layout === 'FULL_WIDTH'
      ? this.SECTION_WIDTH - 10 // Use section width minus small margin for full width
      : (this.CONTENT_RIGHT_MARGIN - this.CONTENT_LEFT_MARGIN) / 2;
    const maxWidth = baseWidth - indentOffset; // Reduce width by indent

    // Use new preprocessing system
    const processedText = this.preprocessText(pdf, `${item.content ?? ''}`, maxWidth, item.label);

    const renderResult = this.renderProcessedText(
      pdf,
      item.label,
      processedText,
      renderX, // Use indented X position
      y,
      y + availableSpace
    );

    if (renderResult.completed) {
      return {
        completed: true,
        usedHeight: renderResult.finalY - y,
        remainingContent: '',
        remainingText: undefined
      };
    } else {
      return {
        completed: false,
        usedHeight: renderResult.finalY - y,
        remainingContent: '', // Legacy field, not used with new system
        remainingText: renderResult.remainingText
      };
    }
  }

  // Render remaining content without label - updated to support new text processing
  private renderItemRemaining(
    pdf: jsPDF,
    item: ContentItem,
    x: number,
    y: number,
    layout: LayoutType,
    remainingContent: string,
    remainingText?: ProcessedText
  ): number {
    // Calculate indent offset to maintain consistent indentation across pages
    const indentOffset = (item.indentLevel || 0) * 10;
    const renderX = x + indentOffset;

    if (remainingText) {
      // Use new system with ProcessedText - apply indentation
      const finalY = this.renderRemainingProcessedText(pdf, remainingText, renderX, y);
      return finalY - y;
    } else {
      // Fallback to old system for backward compatibility - apply indentation
      pdf.setFontSize(10);
      const baseWidth = layout === 'FULL_WIDTH'
        ? this.SECTION_WIDTH - 10 // Use section width minus small margin for full width
        : (this.CONTENT_RIGHT_MARGIN - this.CONTENT_LEFT_MARGIN) / 2;
      const maxWidth = baseWidth - indentOffset; // Reduce width by indent

      const lines = pdf.splitTextToSize(remainingContent, maxWidth);
      pdf.setTextColor(this.CONTENT_COLOR);
      pdf.text(lines, renderX, y); // Use renderX to maintain indentation
      return lines.length * this.LINE_HEIGHT;
    }
  }

  // Unified item rendering method
  private renderItem(
    pdf: jsPDF,
    item: ContentItem,
    x: number,
    y: number,
    layout: LayoutType
  ): number {
    // Handle sub-section titles
    if (item.isSubSectionTitle) {
      pdf.setFontSize(11);  // Slightly smaller font
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(51, 65, 85); // #334155 - same color as main section titles
      pdf.text(item.label, x + 5, y);  // Small indent for sub-section title
      pdf.setFont('helvetica', 'normal');

      let totalHeight = this.LINE_HEIGHT;

      // Draw divider line if requested
      if (item.showDividerLine) {
        const dividerY = y + this.LINE_HEIGHT + 1; // Small gap after title
        this.drawSubSectionDivider(pdf, x + 5, dividerY); // Same x position as title
        totalHeight += 4; // Add height for divider line and spacing
      }

      return totalHeight + 3;  // Add some space after title/divider
    }

    // Calculate indent offset
    const indentOffset = (item.indentLevel || 0) * 10;  // 10 units per indent level
    const renderX = x + indentOffset;

    // Set colors and font
    pdf.setFontSize(10);

    // Render label
    pdf.setTextColor(this.LABEL_COLOR);
    pdf.setFont('helvetica', 'bold'); // Set label font to bold
    pdf.text(`${item.label}:`, renderX, y);
    pdf.setFont('helvetica', 'normal'); // Reset font to normal

    // Calculate content width based on layout and indent
    const baseWidth = layout === 'FULL_WIDTH'
      ? this.SECTION_WIDTH - 10 // Use section width minus small margin for full width
      : (this.CONTENT_RIGHT_MARGIN - this.CONTENT_LEFT_MARGIN) / 2;
    const maxWidth = baseWidth - indentOffset; // Reduce width by indent

    // Render content
    pdf.setTextColor(this.CONTENT_COLOR);
    const contentText = `${item.content ?? ''}`;
    const lines = pdf.splitTextToSize(contentText, maxWidth);
    pdf.text(lines, renderX, y + this.LINE_HEIGHT);

    // Return height for full-width items
    return this.LINE_HEIGHT + (lines.length * this.LINE_HEIGHT);
  }

  // Simplified height calculation
  private calculateSectionHeight(
    pdf: jsPDF,
    items: ContentItem[]
  ): number {
    let currentHeight = this.SECTION_PADDING_TOP; // Add top padding
    let i = 0;

    while (i < items.length) {
      if (items[i].layout === 'FULL_WIDTH') {
        // Use the calculateItemHeight method to handle sub-section titles and indentation
        const itemHeight = this.calculateItemHeight(pdf, items[i]);
        currentHeight += itemHeight + this.CONTENT_SPACING;
        i++;
      } else {
        // Fixed height for two-column items
        currentHeight += this.TWO_COLUMN_HEIGHT + this.TWO_COLUMN_SPACING;
        i += 2;
      }
    }

    return currentHeight + this.SECTION_PADDING_BOTTOM; // Add bottom padding
  }

  generatePositionPdf(params: { companyId: string; positionId: string }): Promise<void> {
    return new Promise((resolve, reject) => {
      this.companyService
        .getPosition(params.companyId, params.positionId)
        .pipe(
          tap((position: Position) => {
            try {
              this.createPdf(position);
              resolve();
            } catch (error) {
              console.error('Error generating PDF:', error);
              reject(error);
            }
          })
        )
        .subscribe({
          error: (error) => {
            console.error('Error loading data:', error);
            reject(error);
          }
        });
    });
  }

  private createPdf(position: Position): void {
    const pdf = new jsPDF();
    let yPos = this.TOP_MARGIN;

    // Add header
    yPos = this.addHeader(pdf, position, yPos);

    // Add basic information
    yPos = this.addBasicInfo(pdf, position, yPos);

    // Add worksite address
    yPos = this.addWorksiteAddress(pdf, position, yPos);

    // Add job details
    yPos = this.addJobDetails(pdf, position, yPos);

    // Add other requirements
    yPos = this.addOtherRequirements(pdf, position, yPos);

    // Add footer to all pages
    this.addFooter(pdf);

    // Open PDF in new tab
    this.openPdf(pdf, position.title);
  }

  private addHeader(pdf: jsPDF, position: Position, startY: number): number {
    // Logo constants
    const logoBase64 = 'data:image/jpeg;base64,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';
    const logoHeight = 15;
    const logoWidth = 35; // Approximate width based on aspect ratio

    // Add logo on the left
    pdf.addImage(logoBase64, 'JPG', this.LEFT_MARGIN, startY, logoWidth, logoHeight);

    // Add job title on the right
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(51, 65, 85); // #334155 - consistent with section titles
    const jobTitleText = `Job Title: ${position.title || 'N/A'}`;
    const pageWidth = pdf.internal.pageSize.width;
    const textWidth = pdf.getTextWidth(jobTitleText);
    const rightX = pageWidth - this.RIGHT_MARGIN - textWidth;
    pdf.text(jobTitleText, rightX, startY + 10); // Centered vertically with logo

    // Reset font
    pdf.setFont('helvetica', 'normal');
    pdf.setTextColor(0, 0, 0);

    return startY + logoHeight + 10; // Add some spacing after header
  }

  private isContentOverflowing(currentY: number, requiredHeight: number, pdf: jsPDF): boolean {
    const pageHeight = pdf.internal.pageSize.height;
    return (currentY + requiredHeight) > (pageHeight - this.BOTTOM_MARGIN);
  }

  private drawSectionWithBorder(
    pdf: jsPDF,
    title: string,
    startY: number,
    height: number,
    options: {
      skipTopBorder?: boolean
    } = {}
  ): void {
    const cornerRadius = 1;
    const startX = this.LEFT_MARGIN;
    const width = this.SECTION_WIDTH;

    // Set border style
    pdf.setDrawColor(226, 232, 240); // #E2E8F0
    pdf.setLineWidth(0.5);

    if (options.skipTopBorder) {
      // Draw borders separately when skipping top border
      // Left border
      pdf.line(startX, startY, startX, startY + height);
      // Right border
      pdf.line(startX + width, startY, startX + width, startY + height);
      // Bottom border
      pdf.line(startX, startY + height, startX + width, startY + height);
    } else {
      // Draw complete rounded rectangle
      pdf.roundedRect(startX, startY, width, height, cornerRadius, cornerRadius);

      // Add the title with larger spacing from top and bottom
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold'); // Set font to bold
      pdf.setTextColor(51, 65, 85); // #334155
      const titleY = startY + this.TITLE_HEIGHT;
      pdf.text(title, startX + 5, titleY);
      pdf.setFont('helvetica', 'normal'); // Reset font to normal

      // Draw horizontal line below title
      const lineY = titleY + this.DIVIDER_SPACING;
      pdf.line(startX, lineY, startX + width, lineY);
    }

    // Reset colors for content
    pdf.setDrawColor(0);
    pdf.setTextColor(0);
  }

  private addBasicInfo(pdf: jsPDF, position: Position, startY: number): number {
    const items: ContentItem[] = [
      {
        label: 'Job Title',
        content: position.title,
        layout: 'TWO_COLUMNS'
      },
      {
        label: 'Job Status',
        content: position.jobStatus ? 'Active' : 'Inactive',
        layout: 'TWO_COLUMNS'
      }
    ];

    const sectionHeight = this.calculateSectionHeight(pdf, items) + this.SECTION_TITLE_HEIGHT;

    // Draw section with border
    this.drawSectionWithBorder(pdf, 'Basic Information', startY, sectionHeight);

    // Render content items
    this.renderContentItems(pdf, items, startY + this.CONTENT_START_OFFSET);

    return startY + sectionHeight + this.SECTION_MARGIN;
  }

  private addWorksiteAddress(pdf: jsPDF, position: Position, startY: number): number {
    if (!position.addresses?.length) {
      const items: ContentItem[] = [
        {
          label: 'Address',
          content: 'No addresses found',
          layout: 'FULL_WIDTH'
        }
      ];

      const sectionHeight = this.calculateSectionHeight(pdf, items) + this.SECTION_TITLE_HEIGHT;

      // Check if section fits in current page
      if (this.isContentOverflowing(startY, sectionHeight, pdf)) {
        pdf.addPage();
        startY = this.TOP_MARGIN;
      }

      // Draw section with border
      this.drawSectionWithBorder(pdf, 'Worksite Address', startY, sectionHeight);

      // Render content items
      this.renderContentItems(pdf, items, startY + this.CONTENT_START_OFFSET);

      return startY + sectionHeight + this.SECTION_MARGIN;
    }

    let currentY = startY;
    position.addresses.forEach((address, index) => {
      const streetNo = address.streetNo || '';
      const street = address.street || '';
      const suiteType = address.suiteType || '';
      const suite = address.suite || '';
      const city = address.city || '';
      const state = address.state || '';
      const county = address.county || '';
      const country = address.country || '';
      const zip = address.zip || '';

      const items: ContentItem[] = [
        {
          label: 'Worksite Street Number',
          content: streetNo,
          layout: 'TWO_COLUMNS'
        },
        {
          label: 'Worksite Street Name',
          content: street,
          layout: 'TWO_COLUMNS'
        },
        {
          label: 'Worksite Apt./Ste./Flr.',
          content: this.getSuiteTypeLabel(address.suiteType),
          layout: 'TWO_COLUMNS'
        },
        {
          label: 'Worksite Apt./Ste./Flr. No.',
          content: suite,
          layout: 'TWO_COLUMNS'
        },
        {
          label: 'Worksite City',
          content: city,
          layout: 'TWO_COLUMNS'
        },
        {
          label: 'Worksite State',
          content: state,
          layout: 'TWO_COLUMNS'
        },
        {
          label: 'Worksite County',
          content: county,
          layout: 'TWO_COLUMNS'
        },
        {
          label: 'Worksite Country',
          content: country,
          layout: 'TWO_COLUMNS'
        },
        {
          label: 'Worksite Zip Code',
          content: zip,
          layout: 'TWO_COLUMNS'
        }
      ];

      const sectionHeight = this.calculateSectionHeight(pdf, items) + this.SECTION_TITLE_HEIGHT;

      // Check if address section fits in current page
      if (this.isContentOverflowing(currentY, sectionHeight, pdf)) {
        pdf.addPage();
        currentY = this.TOP_MARGIN;
      }

      // Draw section with border
      this.drawSectionWithBorder(
        pdf,
        index === 0 ? 'Worksite Address' : `Worksite Address #${index + 1}`,
        currentY,
        sectionHeight
      );

      // Render content items
      this.renderContentItems(pdf, items, currentY + this.CONTENT_START_OFFSET);

      currentY += sectionHeight + this.SECTION_MARGIN;
    });

    return currentY;
  }

  private addJobDetails(pdf: jsPDF, position: Position, startY: number): number {
    // Create ContentItem array for job details - all fields use FULL_WIDTH to match Position Detail page
    const items: ContentItem[] = [
      {
        label: 'Title of Supervisor',
        content: position.supervisorTitle || '',
        layout: 'FULL_WIDTH'
      },
      {
        label: 'Does Position Supervise the Work of other Employees?',
        content: position.superviseOtherEmps == null ? '' :
                 position.superviseOtherEmps ? 'Yes' : 'No',
        layout: 'FULL_WIDTH'
      }
    ];

    // Add SOC codes if superviseOtherEmps is true and socCodes exist
    if (position.superviseOtherEmps === true && position.socCodes && position.socCodes.length > 0) {
      const socCodesText = position.socCodes
        .map(soc => soc.displayName || `${soc.code} - ${soc.title}`)
        .join('; ');

      items.push({
        label: 'SOC Code(s)/Title(s) of the occupation of the employees to be supervised',
        content: socCodesText,
        layout: 'FULL_WIDTH'
      });
    } else if (position.superviseOtherEmps === true) {
      items.push({
        label: 'SOC Code(s)/Title(s) of the occupation of the employees to be supervised',
        content: 'No SOC Code(s) or Title(s) provided.',
        layout: 'FULL_WIDTH'
      });
    }

    // Add duties
    items.push({
      label: 'Duties',
      content: position.duties || '',
      layout: 'FULL_WIDTH'
    });

    // Add special skills question
    items.push({
      label: 'Special Skills or Other Requirements?',
      content: position.specialSkillsEnabled == null ? '' :
               position.specialSkillsEnabled ? 'Yes' : 'No',
      layout: 'FULL_WIDTH'
    });

    // Add special skills details if enabled
    if (position.specialSkillsEnabled === true) {
      if (position.licenseEnabled === true && position.license) {
        items.push({
          label: '(i) License/Certification',
          content: position.license,
          layout: 'FULL_WIDTH'
        });
      }

      if (position.foreignLangEnabled === true && position.foreignLang) {
        items.push({
          label: '(ii) Foreign Language',
          content: position.foreignLang,
          layout: 'FULL_WIDTH'
        });
      }

      if (position.residencyEnabled === true && position.residency) {
        items.push({
          label: '(iii) Residency/Fellowship',
          content: position.residency,
          layout: 'FULL_WIDTH'
        });
      }

      if (position.otherSkillsEnabled === true && position.otherSkills) {
        items.push({
          label: '(iv) Other Special Skills or Requirements',
          content: position.otherSkills,
          layout: 'FULL_WIDTH'
        });
      }
    }

    // Add Primary Education sub-section with divider line
    items.push({
      label: 'Primary Education',
      content: '',
      layout: 'FULL_WIDTH',
      isSubSectionTitle: true,
      showDividerLine: true
    });

    // Education: Minimum U.S. Degree Required
    items.push({
      label: 'Education: Minimum U.S. Degree Required',
      content: this.getEducationLevel(position.minUSDegree || 0),
      layout: 'FULL_WIDTH',
      indentLevel: 1
    });

    // Major(s) field - show based on HTML logic: when minUSDegreeMajors has content
    if (position.minUSDegreeMajors) {
      items.push({
        label: 'Indicate the Major(s) and/or field of study required',
        content: position.minUSDegreeMajors,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Second degree required
    items.push({
      label: 'Does the employer require a second U.S. degree?',
      content: position.secondUSDegreeRequired == null ? '' :
               position.secondUSDegreeRequired ? 'Yes' : 'No',
      layout: 'FULL_WIDTH',
      indentLevel: 1
    });

    // Second degree details - show if required
    if (position.secondUSDegreeRequired === true && position.secondUSDegreeMajors) {
      items.push({
        label: 'Include the second U.S. degree and the major(s) and/or field(s) of study required',
        content: position.secondUSDegreeMajors,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Training required
    items.push({
      label: 'Is Training for the job opportunity required?',
      content: position.trainingRequired == null ? '' :
               position.trainingRequired ? 'Yes' : 'No',
      layout: 'FULL_WIDTH',
      indentLevel: 1
    });

    // Training months - show if training required
    if (position.trainingRequired === true && position.trainingMonths !== undefined) {
      items.push({
        label: 'Specify the number of months of primary training required',
        content: position.trainingMonths,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Training fields - show if training required
    if (position.trainingRequired === true && position.trainingFields) {
      items.push({
        label: 'Indicate the field(s)/name(s) of training required',
        content: position.trainingFields,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Experience required
    items.push({
      label: 'Is employment experience required?',
      content: position.experienceRequired == null ? '' :
               position.experienceRequired ? 'Yes' : 'No',
      layout: 'FULL_WIDTH',
      indentLevel: 1
    });

    // Experience months - show if experience required
    if (position.experienceRequired === true && position.experienceMonths !== undefined) {
      items.push({
        label: 'Months of experience required',
        content: position.experienceMonths,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Occupation - show if experience required
    if (position.experienceRequired === true && position.occupation) {
      items.push({
        label: 'Indicate the occupation required',
        content: position.occupation,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Add Alternative Education sub-section with divider line
    items.push({
      label: 'Alternative Education',
      content: '',
      layout: 'FULL_WIDTH',
      isSubSectionTitle: true,
      showDividerLine: true
    });

    // Alternative Education Accepted - always show
    items.push({
      label: 'Are alternate sets of education, training, and/or experience accepted?',
      content: position.altEduAccepted == null ? '' :
               position.altEduAccepted ? 'Yes' : 'No',
      layout: 'FULL_WIDTH',
      indentLevel: 1
    });

    // Alternative Education Level - show if altEduAccepted is true
    if (position.altEduAccepted === true) {
      items.push({
        label: 'Specify the alternate level of education U.S. degree accepted',
        content: this.getEducationLevel(position.altUSDegree || 0),
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Alternative Major Field - show if altEduAccepted is true and altUSDegree has value
    if (position.altEduAccepted === true && position.altUSDegreeMajors) {
      items.push({
        label: 'Include the major(s) and/or field(s) of study accepted',
        content: position.altUSDegreeMajors,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Alternative Training Accepted - show if altEduAccepted is true
    if (position.altEduAccepted === true) {
      items.push({
        label: 'Alternate training for the job opportunity accepted?',
        content: position.altTrainingAccepted === true ? 'Yes' : 'No',
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Alternative Training Months - show if altEduAccepted and altTrainingAccepted are true
    if (position.altEduAccepted === true && position.altTrainingAccepted === true && position.altTrainingMonths !== undefined) {
      items.push({
        label: 'Months of training accepted',
        content: position.altTrainingMonths,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Alternative Experience Accepted - show if altEduAccepted is true
    if (position.altEduAccepted === true) {
      items.push({
        label: 'Is alternate employment experience accepted?',
        content: position.altExperienceAccepted === true ? 'Yes' : 'No',
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Alternative Experience Months - show if altEduAccepted and altExperienceAccepted are true
    if (position.altEduAccepted === true && position.altExperienceAccepted === true && position.altExperienceMonths !== undefined) {
      items.push({
        label: 'Months of experience accepted',
        content: position.altExperienceMonths,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Alternative Training Fields - show if altEduAccepted and altExperienceAccepted are true
    if (position.altEduAccepted === true && position.altExperienceAccepted === true && position.altTrainingFields) {
      items.push({
        label: 'Include the field(s)/name(s) of experience accepted',
        content: position.altTrainingFields,
        layout: 'FULL_WIDTH',
        indentLevel: 1
      });
    }

    // Calculate total content height to determine if section will span pages
    const contentHeight = this.calculateSectionHeight(pdf, items);
    const totalSectionHeight = contentHeight + this.SECTION_TITLE_HEIGHT;

    // Check if the entire section fits in current page
    const pageHeight = pdf.internal.pageSize.height;
    const availableHeight = pageHeight - this.BOTTOM_MARGIN - startY;

    if (totalSectionHeight <= availableHeight) {
      // Section fits completely - draw full border and render content
      this.drawSectionWithBorder(pdf, 'Job Details', startY, totalSectionHeight);
      this.renderContentItems(pdf, items, startY + this.CONTENT_START_OFFSET);
      return startY + totalSectionHeight + this.SECTION_MARGIN;
    } else {
      // Section will span pages - use universal system
      return this.renderSectionWithPagination(pdf, 'Job Details', items, startY);
    }
  }





  private addOtherRequirements(pdf: jsPDF, position: Position, startY: number): number {
    // Create ContentItem array for other requirements - use FULL_WIDTH to match addJobDetails style
    const items: ContentItem[] = [];

    // Add telecommuting status - match HTML label exactly
    items.push({
      label: 'Telecommuting Permit Required',
      content: position.telecommutingAllowed == null ? '' :
               position.telecommutingAllowed ? 'Yes' : 'No',
      layout: 'FULL_WIDTH'
    });

    // Add telecommuting details if allowed - match HTML label exactly
    if (position.telecommutingAllowed && position.telecommutingBenefit) {
      items.push({
        label: 'Description of the telecommuting benefit/requirement',
        content: position.telecommutingBenefit,
        layout: 'FULL_WIDTH'
      });
    }

    // Add travel status - match HTML label exactly
    items.push({
      label: 'Travel Required',
      content: position.travelRequired == null ? '' :
               position.travelRequired ? 'Yes' : 'No',
      layout: 'FULL_WIDTH'
    });

    // Add travel requirements if required - match HTML label exactly
    if (position.travelRequired && position.travelRequirements) {
      items.push({
        label: 'Description of the travel requirement',
        content: position.travelRequirements,
        layout: 'FULL_WIDTH'
      });
    }

    // Add suggested SOC code - always show, display empty if no value
    let socCodeDisplay = '';
    if (position.suggestedSOCCode) {
      if (position.suggestedSOCCode.code && position.suggestedSOCCode.title) {
        socCodeDisplay = `${position.suggestedSOCCode.code} - ${position.suggestedSOCCode.title}`;
      } else if (position.suggestedSOCCode.displayName) {
        socCodeDisplay = position.suggestedSOCCode.displayName;
      }
    }

    items.push({
      label: 'Suggested SOC Code',
      content: socCodeDisplay,
      layout: 'FULL_WIDTH'
    });

    // Calculate total content height to determine if section will span pages
    const contentHeight = this.calculateSectionHeight(pdf, items);
    const totalSectionHeight = contentHeight + this.SECTION_TITLE_HEIGHT;

    // Check if the entire section fits in current page
    const pageHeight = pdf.internal.pageSize.height;
    const availableHeight = pageHeight - this.BOTTOM_MARGIN - startY;

    if (totalSectionHeight <= availableHeight) {
      // Section fits completely - draw full border and render content
      this.drawSectionWithBorder(pdf, 'Other', startY, totalSectionHeight);
      this.renderContentItems(pdf, items, startY + this.CONTENT_START_OFFSET);
      return startY + totalSectionHeight + this.SECTION_MARGIN;
    } else {
      // Section will span pages - use universal system
      return this.renderSectionWithPagination(pdf, 'Other', items, startY);
    }
  }


  private addFooter(pdf: jsPDF): void {
    const pageCount = pdf.internal.pages.length - 1;
    const pageHeight = pdf.internal.pageSize.height;
    const generatedTime = new Date().toLocaleString();

    pdf.setFontSize(8);
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.text(`Page ${i} of ${pageCount}`, pdf.internal.pageSize.width - this.FOOTER_RIGHT_OFFSET, pageHeight - this.FOOTER_MARGIN);
    }
  }

  private openPdf(pdf: jsPDF, positionTitle?: string): void {
    const fileName = `Position_Summary_${positionTitle?.replace(/[^a-z0-9]/gi, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
    const blob = new Blob([pdf.output('blob')], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);

    const newTab = window.open(blobUrl);
    if (newTab) {
      newTab.document.title = fileName;
    }
  }

  private addMultiLineText(pdf: jsPDF, label: string, text: string, x: number, startY: number): number {
    const maxWidth = this.SECTION_WIDTH;
    pdf.text(label, x, startY);
    startY += this.LINE_HEIGHT;

    const lines = pdf.splitTextToSize(text || '', maxWidth);
    pdf.text(lines, x, startY);

    return startY + (lines.length * this.LINE_HEIGHT);
  }

  // ========================================
  // UNIVERSAL SECTION RENDERING SYSTEM
  // ========================================

  // Universal section rendering with pagination support
  private renderSectionWithPagination(
    pdf: jsPDF,
    sectionTitle: string,
    items: ContentItem[],
    startY: number,
    options: SectionRenderOptions = {}
  ): number {
    // Calculate pagination requirements
    const paginationInfo = this.calculateSectionPagination(pdf, items, startY);

    if (!paginationInfo.needsPagination || options.forceSpanning === false) {
      // Single page rendering
      return this.renderSinglePageSection(pdf, sectionTitle, items, startY, options);
    } else {
      // Multi-page rendering with unified borders
      return this.renderMultiPageSection(pdf, sectionTitle, items, startY, options);
    }
  }

  // Calculate if section needs pagination
  private calculateSectionPagination(
    pdf: jsPDF,
    items: ContentItem[],
    startY: number
  ): SectionPaginationInfo {
    const pageHeight = pdf.internal.pageSize.height;
    const contentHeight = this.calculateSectionHeight(pdf, items);
    const totalSectionHeight = contentHeight + this.SECTION_TITLE_HEIGHT;
    const availableHeight = pageHeight - this.BOTTOM_MARGIN - startY;

    return {
      needsPagination: totalSectionHeight > availableHeight,
      totalHeight: totalSectionHeight,
      availableHeight: availableHeight
    };
  }

  // Render single page section with complete border
  private renderSinglePageSection(
    pdf: jsPDF,
    sectionTitle: string,
    items: ContentItem[],
    startY: number,
    options: SectionRenderOptions
  ): number {
    const contentHeight = this.calculateSectionHeight(pdf, items);
    const totalSectionHeight = contentHeight + this.SECTION_TITLE_HEIGHT;

    // Draw unified border for entire section
    this.drawUnifiedSectionBorder(pdf, sectionTitle, startY, totalSectionHeight, 'COMPLETE', options.borderStyle);

    // Render content
    if (options.customContentRenderer) {
      const contentEndY = options.customContentRenderer(pdf, items, startY + this.CONTENT_START_OFFSET, startY + totalSectionHeight);
      return contentEndY + this.SECTION_MARGIN;
    } else {
      this.renderContentItems(pdf, items, startY + this.CONTENT_START_OFFSET);
      return startY + totalSectionHeight + this.SECTION_MARGIN;
    }
  }

  // Render multi-page section with connected borders - SIMPLIFIED AND FIXED
  private renderMultiPageSection(
    pdf: jsPDF,
    sectionTitle: string,
    items: ContentItem[],
    startY: number,
    options: SectionRenderOptions
  ): number {
    // Use a simpler approach: track where content goes and draw borders afterwards
    const pageStartPositions: number[] = [];
    const pageEndPositions: number[] = [];
    const startPageNumber = pdf.internal.pages.length - 1;

    // First, draw the section title and get the content start position
    this.drawSectionTitle(pdf, sectionTitle, this.LEFT_MARGIN, startY);
    const contentStartY = startY + this.CONTENT_START_OFFSET;

    // Record the first page start
    pageStartPositions.push(startY);

    // Render content using existing renderContentItems with custom tracking
    const finalY = this.renderContentItemsWithPageTracking(pdf, items, contentStartY, pageStartPositions, pageEndPositions);

    // Now draw borders for each page
    const endPageNumber = pdf.internal.pages.length - 1;

    for (let pageIndex = 0; pageIndex < pageStartPositions.length; pageIndex++) {
      const currentPageNum = startPageNumber + pageIndex;
      pdf.setPage(currentPageNum);

      const isFirstPage = pageIndex === 0;
      const isLastPage = pageIndex === pageStartPositions.length - 1;
      const pageStart = pageStartPositions[pageIndex];
      const pageEnd = pageEndPositions[pageIndex];

      // Calculate correct page height based on actual content positions
      let pageHeight: number;
      if (isLastPage) {
        // For last page, use finalY as the end position
        pageHeight = finalY - pageStart;
      } else {
        // For other pages, use recorded pageEnd
        pageHeight = pageEnd - pageStart;
      }

      if (isFirstPage && isLastPage) {
        // Single page - complete border
        this.drawUnifiedSectionBorder(pdf, '', pageStart, pageHeight, 'COMPLETE', options.borderStyle);
      } else if (isFirstPage) {
        // First page - top + sides
        this.drawUnifiedSectionBorder(pdf, '', pageStart, pageHeight, 'FIRST_PAGE', options.borderStyle);
      } else if (isLastPage) {
        // Last page - sides + bottom
        this.drawUnifiedSectionBorder(pdf, '', pageStart, pageHeight, 'LAST_PAGE', options.borderStyle);
      } else {
        // Middle page - sides only
        this.drawUnifiedSectionBorder(pdf, '', pageStart, pageHeight, 'MIDDLE_PAGE', options.borderStyle);
      }
    }

    // Return to the last page
    pdf.setPage(endPageNumber);
    return finalY + this.SECTION_MARGIN;
  }

  // Helper method to track page positions during content rendering
  private renderContentItemsWithPageTracking(
    pdf: jsPDF,
    items: ContentItem[],
    startY: number,
    pageStartPositions: number[],
    pageEndPositions: number[]
  ): number {
    const pageHeight = pdf.internal.pageSize.height;
    const maxY = pageHeight - this.BOTTOM_MARGIN;

    let currentY = startY + this.SECTION_PADDING_TOP;
    let i = 0;
    let currentPageIndex = 0;

    while (i < items.length) {
      if (items[i].layout === 'FULL_WIDTH') {
        const itemHeight = this.calculateItemHeight(pdf, items[i]);
        const totalHeight = itemHeight + this.CONTENT_SPACING;

        if (currentY + totalHeight > maxY) {
          // Need page break
          const availableSpace = maxY - currentY;
          if (availableSpace > this.LINE_HEIGHT * 3) {
            // Split the item
            const splitResult = this.renderItemWithSplit(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'FULL_WIDTH', availableSpace);

            if (!splitResult.completed) {
              // Record end of current page BEFORE moving to next page
              pageEndPositions[currentPageIndex] = currentY + splitResult.usedHeight;

              // Move to next page
              pdf.addPage();
              currentY = this.TOP_MARGIN + this.SECTION_PADDING_TOP;
              currentPageIndex++;
              pageStartPositions.push(this.TOP_MARGIN);

              // Render remaining content
              const remainingHeight = this.renderItemRemaining(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'FULL_WIDTH', splitResult.remainingContent, splitResult.remainingText);
              currentY += remainingHeight + this.CONTENT_SPACING;
            } else {
              currentY += splitResult.usedHeight + this.CONTENT_SPACING;
            }
          } else {
            // Record end of current page BEFORE moving to next page
            pageEndPositions[currentPageIndex] = currentY;

            // Move entire item to next page
            pdf.addPage();
            currentY = this.TOP_MARGIN + this.SECTION_PADDING_TOP;
            currentPageIndex++;
            pageStartPositions.push(this.TOP_MARGIN);

            const height = this.renderItem(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'FULL_WIDTH');
            currentY += height + this.CONTENT_SPACING;
          }
        } else {
          // Render normally
          const height = this.renderItem(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'FULL_WIDTH');
          currentY += height + this.CONTENT_SPACING;
        }
        i++;
      } else {
        // Handle two-column items similarly
        const requiredHeight = this.TWO_COLUMN_HEIGHT + this.TWO_COLUMN_SPACING;
        if (currentY + requiredHeight > maxY) {
          pageEndPositions[currentPageIndex] = currentY;
          pdf.addPage();
          currentY = this.TOP_MARGIN + this.SECTION_PADDING_TOP;
          currentPageIndex++;
          pageStartPositions.push(this.TOP_MARGIN);
        }

        this.renderItem(pdf, items[i], this.CONTENT_LEFT_MARGIN, currentY, 'TWO_COLUMNS');
        if (i + 1 < items.length && items[i + 1].layout === 'TWO_COLUMNS') {
          this.renderItem(pdf, items[i + 1], this.CONTENT_RIGHT_MARGIN, currentY, 'TWO_COLUMNS');
          i += 2;
        } else {
          i++;
        }
        currentY += this.TWO_COLUMN_HEIGHT + this.TWO_COLUMN_SPACING;
      }
    }

    // Record final page end - include bottom padding
    pageEndPositions[currentPageIndex] = currentY + this.SECTION_PADDING_BOTTOM;

    return currentY + this.SECTION_PADDING_BOTTOM;
  }

  // Unified border drawing for all border types - UPDATED TO MATCH drawSectionWithBorder
  private drawUnifiedSectionBorder(
    pdf: jsPDF,
    title: string,
    startY: number,
    height: number,
    borderType: BorderType,
    customStyle?: { color?: string; width?: number; cornerRadius?: number }
  ): void {
    const startX = this.LEFT_MARGIN;
    const width = this.SECTION_WIDTH;
    const cornerRadius = customStyle?.cornerRadius ?? 3;

    // Use EXACT same border style as drawSectionWithBorder for consistency
    pdf.setDrawColor(226, 232, 240); // #E2E8F0 - exact same as drawSectionWithBorder
    pdf.setLineWidth(0.5); // exact same line width

    switch (borderType) {
      case 'COMPLETE':
        // Draw complete rounded rectangle with title - IDENTICAL to drawSectionWithBorder
        pdf.roundedRect(startX, startY, width, height, cornerRadius, cornerRadius);

        // Add section title with same styling as drawSectionWithBorder
        if (title) {
          pdf.setFontSize(12);
          pdf.setTextColor(51, 65, 85); // #334155
          const titleY = startY + this.TITLE_HEIGHT;
          pdf.text(title, startX + 5, titleY);

          // Draw horizontal line below title
          const lineY = titleY + this.DIVIDER_SPACING;
          pdf.line(startX, lineY, startX + width, lineY);
        }
        break;

      case 'FIRST_PAGE':
        // Top rounded corners + sides + title (no bottom)
        this.drawTopBorder(pdf, startX, startY, width, cornerRadius);
        pdf.line(startX, startY + cornerRadius, startX, startY + height); // Left side
        pdf.line(startX + width, startY + cornerRadius, startX + width, startY + height); // Right side

        // Section title is already drawn separately before this method
        break;

      case 'MIDDLE_PAGE':
        // Only left and right sides - no rounded corners needed
        pdf.line(startX, startY, startX, startY + height); // Left side
        pdf.line(startX + width, startY, startX + width, startY + height); // Right side
        break;

      case 'LAST_PAGE':
        // Sides + bottom rounded corners (no top)
        pdf.line(startX, startY, startX, startY + height - cornerRadius); // Left side
        pdf.line(startX + width, startY, startX + width, startY + height - cornerRadius); // Right side
        this.drawBottomBorder(pdf, startX, startY + height, width, cornerRadius);
        break;
    }

    // Reset drawing settings - same as drawSectionWithBorder
    pdf.setDrawColor(0);
    pdf.setTextColor(0);
  }

  // Helper methods for border drawing with proper rounded corners
  private drawTopBorder(pdf: jsPDF, x: number, y: number, width: number, radius: number): void {
    // Draw top border with rounded corners using path
    // Top-left rounded corner
    pdf.moveTo(x, y + radius);
    pdf.curveTo(x, y, x, y, x + radius, y);

    // Top horizontal line
    pdf.lineTo(x + width - radius, y);

    // Top-right rounded corner
    pdf.curveTo(x + width, y, x + width, y, x + width, y + radius);
    pdf.stroke();
  }

  private drawBottomBorder(pdf: jsPDF, x: number, y: number, width: number, radius: number): void {
    // Draw bottom border with rounded corners using path
    // Start from bottom-left
    pdf.moveTo(x, y - radius);

    // Bottom-left rounded corner
    pdf.curveTo(x, y, x, y, x + radius, y);

    // Bottom horizontal line
    pdf.lineTo(x + width - radius, y);

    // Bottom-right rounded corner
    pdf.curveTo(x + width, y, x + width, y, x + width, y - radius);
    pdf.stroke();
  }

  /**
   * Draw sub-section divider line under sub-section titles
   */
  private drawSubSectionDivider(pdf: jsPDF, x: number, y: number): void {
    // Set divider line style - same color as main section borders but slightly thinner
    pdf.setDrawColor(226, 232, 240); // #E2E8F0 - same as border color
    pdf.setLineWidth(0.3); // Slightly thinner than main borders

    // Draw horizontal line - shorter than main section width to show hierarchy
    pdf.line(x, y, x + this.SUB_SECTION_DIVIDER_LENGTH, y);

    // Reset drawing settings
    pdf.setDrawColor(0);
    pdf.setLineWidth(0.5);
  }

  private drawSectionTitle(pdf: jsPDF, title: string, x: number, y: number): void {
    if (title) {
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold'); // Set font to bold
      pdf.setTextColor(51, 65, 85); // #334155
      const titleY = y + this.TITLE_HEIGHT;
      pdf.text(title, x + 5, titleY);
      pdf.setFont('helvetica', 'normal'); // Reset font to normal

      // Draw horizontal line below title with correct color
      const lineY = titleY + this.DIVIDER_SPACING;
      pdf.setDrawColor(226, 232, 240); // #E2E8F0 - same as border color
      pdf.setLineWidth(0.5); // same as border width
      pdf.line(x, lineY, x + this.SECTION_WIDTH, lineY);

      // Reset drawing color
      pdf.setDrawColor(0);
    }
  }

  private getEducationLevel(level: number | null | undefined): string {
    // Handle null/undefined values
    if (level == null) {
      return '';
    }

    // Use FNEducationList to find the label
    const educationLevel = FNEducationList.find(option => option.value === level);
    return educationLevel ? educationLevel.label : 'Unknown';
  }

  private getSuiteTypeLabel(suiteType: number | null | undefined): string {
    if (suiteType == null) {
      return '';
    }

    const suiteTypeOption = AddressSuiteTypeList.find(option => option.value === suiteType);
    return suiteTypeOption ? suiteTypeOption.label : '';
  }
}
